import _ from "lodash";
import moment from "moment";

/** Bcrypt hashes contain $ which CRA dotenv-expand strips; use REACT_APP_API_KEY_B64 instead. */
const apiKey = () => {
  const b64 = process.env.REACT_APP_API_KEY_B64;
  if (b64) {
    return atob(b64);
  }
  return process.env.REACT_APP_API_KEY || "";
};

const Utils = {
  apiUrl: (path) => process.env.REACT_APP_API_URL + path,

  getLogoUrl: (logo) => {
    if (!logo || typeof logo !== "string" || logo.trim() === "" || logo === "null" || logo === "undefined") {
      return process.env.PUBLIC_URL + "/images/logo.png";
    }
    const cleanLogo = logo.trim();
    if (cleanLogo.startsWith("http://") || cleanLogo.startsWith("https://") || cleanLogo.startsWith("data:")) {
      return cleanLogo;
    }
    // Strip any leading ./, ../, /, uploads/, logos/, or uploads/logos/
    const filename = cleanLogo
      .replace(/^(\.\.\/|\.\/|\/)+/, "")
      .replace(/^(uploads\/logos\/|logos\/|uploads\/)+/, "")
      .replace(/^(\.\.\/|\.\/|\/)+/, "");

    const apiUrl = process.env.REACT_APP_API_URL || "/admin/api/";
    // Strip index.php and api subpaths to get the base admin web root
    const adminBase = apiUrl.replace(/\/index\.php.*$/, "/").replace(/\/api.*$/, "/");
    const baseUrl = adminBase.endsWith("/") ? adminBase : adminBase + "/";
    return `${baseUrl}uploads/logos/${filename}`;
  },

  /** Export data as a branded, styled CSV with UTF-8 BOM for Microsoft Excel / Sheets compatibility */
  exportStyledCsv: ({ title = "Report", headers = [], rows = [], filename = null }) => {
    const userData = Utils.getUserData() || {};
    const practiceName = _.get(userData, "braname") || _.get(userData, "branch_name") || _.get(userData, "practice_name") || _.get(userData, "orgname") || "DenTTrack Practice";
    const userName = _.get(userData, "name", "DenTTrack User");
    const exportTime = moment().format("DD/MM/YYYY HH:mm:ss");

    const escapeCell = (cell) => {
      if (cell === null || cell === undefined) return '""';
      const str = String(cell).replace(/"/g, '""');
      return `"${str}"`;
    };

    const lines = [];
    // Top Branded Metadata Banner
    lines.push([escapeCell("DenTTrack Practice Management System"), escapeCell("")].join(","));
    lines.push([escapeCell("Practice / Branch:"), escapeCell(practiceName)].join(","));
    lines.push([escapeCell("Report:"), escapeCell(title)].join(","));
    lines.push([escapeCell("Generated On:"), escapeCell(exportTime), escapeCell("Exported By:"), escapeCell(userName)].join(","));
    lines.push(""); // blank spacer row

    // Table Column Headers
    lines.push(headers.map(escapeCell).join(","));

    // Table Rows
    rows.forEach((row) => {
      if (Array.isArray(row)) {
        lines.push(row.map(escapeCell).join(","));
      } else if (typeof row === "object" && row !== null) {
        lines.push(headers.map((h) => escapeCell(row[h] ?? "")).join(","));
      }
    });

    const csvContent = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const finalFilename = filename || `${title.toLowerCase().replace(/[^a-z0-9]+/g, "_")}_${moment().format("YYYY-MM-DD")}.csv`;

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", finalFilename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /** When PHP returns JSON without application/json, axios leaves res.data as a string. */
  parseApiResponse(res) {
    let d = res.data;
    if (typeof d !== "string") {
      return d;
    }
    let s = d.trim().replace(/^\uFEFF/, "");
    const i = s.indexOf("{");
    if (i > 0) {
      s = s.slice(i);
    }
    return JSON.parse(s);
  },

  viewImage(base64Data) {
    if (!base64Data) return;
    // Dispatch a custom event – SelfieModal (mounted globally in App.js) will
    // intercept this and show the image in an in-page popup, avoiding a new tab.
    window.dispatchEvent(new CustomEvent("show-selfie-modal", { detail: { src: base64Data } }));
  },

  formatWorkingHours(hours) {
    if (hours === null || hours === undefined || hours === "") return "—";
    const num = parseFloat(hours);
    if (isNaN(num)) return "—";
    const h = Math.floor(num);
    const m = Math.round((num - h) * 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m < 10 ? "0" + m : m}m`;
  },

  apiHeaders: (extraparams = null) => {
    let headers = {
      headers: {
        apikey: apiKey(),
        tzoffset: moment().utcOffset(),
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "PUT,GET,POST,DELETE,OPTIONS,PATCH",
        "Access-Control-Allow-Headers":
          "Origin, Content-Type, X-Auth-Token, token, apikey, tzoffset, allow-origin, Allow-Origin",
        Accept: "application/json",
      },
    };

    const uData = Utils.getUserData();
    if (uData && _.get(uData, "token", false)) {
      headers["headers"]["token"] = uData.token;
    }

    if (extraparams) {
      headers = { ...headers, ...extraparams };
    }
    return headers;
  },

  viewerLink: (fname, path) => {
    let url = Utils.apiUrl(path);
    //if (!Utils.isImgUrl(fname)) url = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}`;
    return url;
  },

  postVars: (pvals) =>
    Object.keys(pvals)
      .map(function (k) {
        return encodeURIComponent(k) + "=" + encodeURIComponent(pvals[k]);
      })
      .join("&"),

  isLoggedIn: () => {
    const dt = Utils.getUserData();
    return _.get(dt, "token", false) !== false;
  },

  removeSession: () => {
    let dt = Utils.getUserData();
    // console.log(dt, "session data")
    dt.token = false;
    // const backup = localStorage.getItem(process.env.REACT_APP_APPNAME + '-userData');
    localStorage.setItem(
      process.env.REACT_APP_APPNAME + "-userData",
      JSON.stringify(dt),
    );
  },

  getUserData: () => {
    let userData = localStorage.getItem(
      process.env.REACT_APP_APPNAME + "-userData",
    );
    if (userData) {
      return JSON.parse(userData);
    } else {
      return false;
    }
  },

  getToken: () => {
    const uData = Utils.getUserData();
    return uData ? _.get(uData, "token", "") : "";
  },

  setUserData: (data) => {
    const uData = data;
    // return;
    if (uData === false) {
      //=== logout
      localStorage.removeItem(process.env.REACT_APP_APPNAME + "-userData");
    } else {
      let userData = Utils.getUserData();
      if (!userData) {
        userData = {};
      }
      userData = { ...userData, ...uData, token: _.get(data, "token", false) };
      localStorage.setItem(
        process.env.REACT_APP_APPNAME + "-userData",
        JSON.stringify(userData),
      );
    }
  },

  addToUserData: (data) => {
    let userData = Utils.getUserData();
    if (!userData) {
      userData = {};
    }
    userData = { ...userData, ...data };
    localStorage.setItem(
      process.env.REACT_APP_APPNAME + "-userData",
      JSON.stringify(userData),
    );
  },

  hasAccess: (id) => {
    let userData = Utils.getUserData();
    if (!userData) return false;

    // Super users always have menu visibility (and see upgrade notice if module not on active plan)
    if (userData.is_super_user === true || userData.is_super_user === 1 || userData.is_super_user === "1" || userData.is_super_user === "true") return true;

    // Normal User module resolution:
    // 1. Gather all plan-allowed module IDs from plan.orgmodules, plan.modules, or planmods
    const planOrgModsRaw = _.get(userData, "plan.orgmodules", null);
    const planModsRaw = _.get(userData, "plan.modules", null);
    const planModsObj = _.get(userData, "planmods", []);

    let planAllowed = [];
    if (planOrgModsRaw) {
      if (Array.isArray(planOrgModsRaw)) planAllowed.push(...planOrgModsRaw);
      else if (typeof planOrgModsRaw === "string") {
        try {
          const parsed = JSON.parse(planOrgModsRaw);
          if (Array.isArray(parsed)) planAllowed.push(...parsed);
          else planAllowed.push(...planOrgModsRaw.split(","));
        } catch (e) {
          planAllowed.push(...planOrgModsRaw.split(","));
        }
      }
    }
    if (planModsRaw) {
      if (Array.isArray(planModsRaw)) planAllowed.push(...planModsRaw);
      else if (typeof planModsRaw === "string") {
        try {
          const parsed = JSON.parse(planModsRaw);
          if (Array.isArray(parsed)) planAllowed.push(...parsed);
          else planAllowed.push(...planModsRaw.split(","));
        } catch (e) {
          planAllowed.push(...planModsRaw.split(","));
        }
      }
    }
    if (Array.isArray(planModsObj) && planModsObj.length > 0) {
      planAllowed.push(...planModsObj.map((m) => m.id));
    }

    // 2. Gather organisation / user assigned modules from userData.modules or userData.has_access
    const userModsRaw = _.get(userData, "modules", "") || _.get(userData, "has_access", "");
    let orgAllowed = [];
    if (Array.isArray(userModsRaw)) {
      orgAllowed = userModsRaw;
    } else if (typeof userModsRaw === "string" && userModsRaw.trim() !== "") {
      try {
        const parsed = JSON.parse(userModsRaw);
        if (Array.isArray(parsed)) orgAllowed = parsed;
        else orgAllowed = userModsRaw.split(",");
      } catch (e) {
        orgAllowed = userModsRaw.split(",");
      }
    }

    const planIds = planAllowed.map((v) => String(v).trim()).filter(Boolean);
    const orgIds = orgAllowed.map((v) => String(v).trim()).filter(Boolean);
    const targetId = String(id).trim();

    // If organisation has assigned modules, and plan has modules:
    // Module is active if it is enabled in plan AND/OR permitted by org configuration
    if (planIds.length > 0 && orgIds.length > 0) {
      return planIds.includes(targetId) || orgIds.includes(targetId);
    } else if (planIds.length > 0) {
      return planIds.includes(targetId);
    } else if (orgIds.length > 0) {
      return orgIds.includes(targetId);
    }

    return false;
  },

  weekdays: () => {
    return [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
  },

  getMonthDates: (mdt) => {
    let dts = [],
      dt,
      idx = 1,
      days = moment(mdt).daysInMonth();

    do {
      dt = moment(mdt).startOf("month").day(idx++).format("YYYY-MM-DD");
      dts.push(dt);
    } while (moment(mdt).month() !== moment(dt).month());

    for (idx = 1; idx < days; idx++) {
      dt = moment(dt).add(1, "d").format("YYYY-MM-DD");
      dts.push(dt);
    }

    for (idx = moment(dt).day(); idx < 7; idx++) {
      dt = moment(dt).add(1, "d").format("YYYY-MM-DD");
      dts.push(dt);
    }
    return dts;
  },

  getAllWeekDates: (mdt, wd) => {
    //wd goes from 1=Monday to 7=Sunday
    if (wd === 7) {
      wd = 0; //== adjusting sunday to 0 - 6
    }

    let dts = [],
      dt;

    for (var idx = 0; ; idx += 7) {
      dt = moment(mdt).day(wd + idx);

      if (moment(dt).isBefore(mdt)) {
        dts.push(null);
      } else {
        if (
          parseInt(moment(dt).format("M")) !== parseInt(moment(mdt).format("M"))
        ) {
          if (
            parseInt(moment(dt).day(1).format("M")) ===
            parseInt(moment(mdt).format("M"))
          ) {
            dts.push(null);
          }
          break;
        }

        dts.push(
          moment(mdt)
            .day(wd + idx)
            .format("YYYY-MM-DD"),
        );
      }
    }

    //== handling Sundays

    if (wd === 0) {
      if (moment(mdt).endOf("month").day() !== "0") {
        dts.push(null); //=== add blank at sunday if month end is not sunday
      }

      if (_.isNull(dts[0])) {
        dts.shift(); //== remove blank from starting for sunday column
      }
    }

    if (parseInt(moment(mdt).format("e")) === 0 && wd !== 0) {
      dts.unshift(null); //== add blank at starting if sunday is the starting day of month
    }

    return dts;
  },

  addZero: (num) => {
    if (num == null) return "00";
    return (num < 10 ? "0" : "") + num;
  },

  getFileIcon: (fname) => {
    const extns = {
      xls: "xls",
      xlsx: "xls",
      pdf: "pdf",
      mp4: "video",
      "3gp": "video",
    };

    const fextn = fname.substring(fname.lastIndexOf(".") + 1);

    return `${process.env.PUBLIC_URL}/images/${_.get(extns, fextn, "Information")}.png`;
  },

  getFileName: (fname) => {
    return fname.substring(0, fname.lastIndexOf("."));
  },

  isImgUrl: (fileName) => {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp"];
    const fileExtension = fileName.split(".").pop().toLowerCase();
    return imageExtensions.includes(fileExtension);
  },

  downloadIcon: (style) => {
    return (
      <i className="bi bi-download" style={{ fontSize: '16px', ...style }}></i>
    );
  },

  deleteIcon: (style) => {
    return (
      <i className="bi bi-trash" style={{ fontSize: '16px', ...style }}></i>
    );
  },

  toLocal: (dt) => {
    let min = parseInt(moment().zone()); // + (moment().isDST() ? 60 : 0);
    //console.log(dt, moment().isDST(), moment().zone(), min);
    return moment(dt).subtract(min, "minutes");
  },

  perpage: 10,

  rotaRooms: () =>
    _.get(process.env, "REACT_APP_ROOM_TYPES", "NoRoom").split(","),

  rotaRoom: (room = 0) => _.get(Utils.rotaRooms(), room, "No Room"),

  viewIcon: (style) => {
    return (
      <i className="bi bi-eye" style={{ fontSize: '16px', ...style }}></i>
    );
  },
  printIcon: (style) => {
    return (
      <i className="bi bi-printer" style={{ fontSize: '16px', ...style }}></i>
    );
  },
  getGapStatusList: () => {
    return [
      [0, "", "Not Defined", "#e0ddddff"],
      [1, "gap_have", "Have This", "#76D773"],
      [2, "gap_working", "Working on it", "#FFBF00"],
      [3, "gap_dont", "Don't Have", "#EC5353"],
      [4, "gap_na", "N/A", "#848484"],
    ];
  },
};

export default Utils;
